import '../../../styles/HomeProducts.scss'
import { Product } from "../../types/Page";
import natural from '../../../assets/images/Our Natural Range.svg';
import { IMAGE_URL } from "../../../path";
import { useEffect, useRef, useState } from "react";
import {useNavigate} from "react-router-dom";

interface HomeProductsProps {
    products: Product[];
}

function HomeProducts({ products }: HomeProductsProps) {
    const [selectedCaps, setSelectedCaps] = useState<Record<number, number>>({});
    const [expandedProduct, setExpandedProduct] = useState<Product | null>(null);
    const navigate = useNavigate();

    /*
    |--------------------------------------------------------------------------
    | CAROUSEL
    |--------------------------------------------------------------------------
    */

    // Start in the middle copy.
    const [currentIndex, setCurrentIndex] = useState(products.length);

    const [disableTransition, setDisableTransition] = useState(false);

    const productsInnerRef = useRef<HTMLDivElement>(null);
    const viewportRef = useRef<HTMLDivElement>(null);

    const [productWidth, setProductWidth] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);

    /*
    |--------------------------------------------------------------------------
    | CALCULATE PRODUCT WIDTH
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const updateProductWidth = () => {
            if (!viewportRef.current) {
                return;
            }

            const viewportWidth =
                viewportRef.current.getBoundingClientRect().width;

            setViewportWidth(viewportWidth);

            let visibleProducts = 5;

            if (window.innerWidth <= 840) {
                visibleProducts = 2;
            } else if (window.innerWidth <= 1000) {
                visibleProducts = 3;
            } else if (window.innerWidth <= 1500) {
                visibleProducts = 4;
            }

            setProductWidth(viewportWidth / visibleProducts);
        };

        updateProductWidth();

        window.addEventListener('resize', updateProductWidth);

        return () => {
            window.removeEventListener('resize', updateProductWidth);
        };
    }, []);

    /*
    |--------------------------------------------------------------------------
    | RESET INDEX WHEN PRODUCTS CHANGE
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        setCurrentIndex(products.length);
    }, [products.length]);

    /*
    |--------------------------------------------------------------------------
    | EXPAND / COLLAPSE
    |--------------------------------------------------------------------------
    */

    function toggleExpandedProduct(product: Product) {
        setExpandedProduct((current) => {
            if (current?.id === product.id) {
                return null;
            }

            return product;
        });
    }

    /*
    |--------------------------------------------------------------------------
    | SCROLL
    |--------------------------------------------------------------------------
    */

    function scrollProducts(direction: 'left' | 'right') {
        if (!products.length) {
            return;
        }

        setCurrentIndex((current) => {
            if (direction === 'right') {
                return current + 1;
            }

            return current - 1;
        });
    }

    /*
    |--------------------------------------------------------------------------
    | INFINITE LOOP
    |--------------------------------------------------------------------------
    */

    function handleTransitionEnd() {
        if (!products.length) {
            return;
        }

        const productCount = products.length;

        /*
        |--------------------------------------------------------------------------
        | RIGHT SIDE
        |--------------------------------------------------------------------------
        |
        | Once we've reached the third copy, silently jump
        | back to the middle copy.
        |
        */

        if (currentIndex >= productCount * 2) {
            setDisableTransition(true);
            setCurrentIndex(productCount);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setDisableTransition(false);
                });
            });

            return;
        }

        /*
        |--------------------------------------------------------------------------
        | LEFT SIDE
        |--------------------------------------------------------------------------
        |
        | Once we've moved into the first copy, silently jump
        | back to the middle copy.
        |
        */

        if (currentIndex < productCount) {
            setDisableTransition(true);
            setCurrentIndex(productCount * 2 - 1);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setDisableTransition(false);
                });
            });
        }
    }

    /*
    |--------------------------------------------------------------------------
    | THREE COPIES
    |--------------------------------------------------------------------------
    */

    const carouselProducts = [
        ...products,
        ...products,
        ...products,
    ];

    /*
    |--------------------------------------------------------------------------
    | MOBILE CENTRED POSITION
    |--------------------------------------------------------------------------
    */

    const isMobile =
        typeof window !== "undefined" && window.innerWidth <= 840;

    const mobileOffset =
        (viewportWidth - productWidth) / 2;

    const translateX = isMobile
        ? -(currentIndex * productWidth) + mobileOffset
        : -(currentIndex * productWidth);

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="natural-range-products-section">

            <div className="natural-range-products-tag">
                <img
                    src={natural}
                    alt="Natural Range"
                />
            </div>

            <div className="natural-range-products">

                {/* LEFT ARROW */}

                <div
                    className="natural-range-products-arrow natural-range-products-arrow-left"
                    onClick={() => scrollProducts('left')}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="38"
                        height="38"
                        viewBox="0 0 38 38"
                    >
                        <circle
                            cx="19"
                            cy="19"
                            r="18"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2"
                        />

                        <path
                            d="M21.5 11.5L14 19L21.5 26.5"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

                {/* PRODUCTS */}

                <div
                    className="natural-range-products-viewport"
                    ref={viewportRef}
                >
                    <div
                        className="natural-range-products-inner"
                        ref={productsInnerRef}
                        onTransitionEnd={handleTransitionEnd}
                        style={{
                            transform: `translateX(${translateX}px)`,

                            transition: disableTransition
                                ? 'none'
                                : 'transform 0.5s ease',
                        }}
                    >

                        {carouselProducts.map((product, index) => {
                            const selectedCap =
                                selectedCaps[product.id] ?? 0;

                            const selectedSize =
                                product.sizes[selectedCap] ??
                                product.sizes[0];

                            const isExpanded =
                                expandedProduct?.id === product.id;

                            return (
                                <div
                                    key={`${product.id}-${index}`}
                                    onClick={() => navigate(`/product/${product.slug}`)}
                                    className={`natural-range-product ${
                                        isExpanded
                                            ? "natural-range-product--expanded"
                                            : ""
                                    }`}
                                    style={{
                                        "--featured-background-color":
                                        product.color_code,

                                        width: `${productWidth}px`,
                                        flex: `0 0 ${productWidth}px`,
                                    } as React.CSSProperties}
                                >

                                    <img
                                        src={`${IMAGE_URL}/${selectedSize.pack_shot}`}
                                        alt={
                                            product.name +
                                            selectedSize.capacity
                                        }
                                    />

                                    <div className="natural-range-product-info">

                                        <h2>
                                            {product.name}
                                        </h2>

                                        <h3>
                                            {`${selectedSize.title} (${selectedSize.capacity})`}
                                        </h3>

                                        {isExpanded && (
                                            <div>

                                                <p>
                                                    {product.description}
                                                </p>

                                                <div className="natural-range-product-capacities">

                                                    {product.sizes.map(
                                                        (size, sizeIndex) => (
                                                            <div
                                                                className={`natural-range-product-capacity ${
                                                                    selectedCap === sizeIndex
                                                                        ? "natural-range-product-capacity--active"
                                                                        : ""
                                                                }`}
                                                                key={sizeIndex}
                                                                onClick={() => {
                                                                    setSelectedCaps(
                                                                        (prev) => ({
                                                                            ...prev,
                                                                            [product.id]:
                                                                            sizeIndex,
                                                                        })
                                                                    );
                                                                }}
                                                            >
                                                                {size.capacity}
                                                            </div>
                                                        )
                                                    )}

                                                </div>

                                            </div>
                                        )}

                                        <div
                                            className="natural-range-product-toggle"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpandedProduct(product);
                                            }}
                                        >
                                            {isExpanded ? '-' : '+'}
                                        </div>

                                    </div>

                                </div>
                            );
                        })}

                    </div>
                </div>

                {/* RIGHT ARROW */}

                <div
                    className="natural-range-products-arrow natural-range-products-arrow-right"
                    onClick={() => scrollProducts('right')}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="38"
                        height="38"
                        viewBox="0 0 38 38"
                    >
                        <circle
                            cx="19"
                            cy="19"
                            r="18"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2"
                        />

                        <path
                            d="M16.5 11.5L24 19L16.5 26.5"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

            </div>

            <div className="natural-range-products-shop-all">
                <a href="/products">
                    View All Products
                </a>
            </div>

        </div>
    );
}

export default HomeProducts;
